'use client';

import useSWR from 'swr';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import { orderApi } from '@/entities/order';
import type { DepartureLocationInfo } from '@/entities/order/model/order.types';
import type { VisitCounselState } from './visit-counsel.types';

export interface MyPlannerDayCounsel {
  visitCounselId: string;
  orderId: string;
  scheduleTime: string;
  location: DepartureLocationInfo | null;
  misoOrderId?: string;
  supplyArea?: number;
  /** CounselState: APPOINTED(시작전), STARTED(진행중) */
  state?: VisitCounselState;
  /** 상담 시작 시각 (state fallback용) */
  startedAt?: string | null;
}

export interface UseMyPlannerDayCounselsResult {
  counsels: MyPlannerDayCounsel[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

async function fetchMyPlannerDayCounsels(
  date: string,
  plannerId: string | undefined
): Promise<MyPlannerDayCounsel[]> {
  const filters: Array<{
    field: 'scheduleDate' | 'counselorId';
    operator: 'EQUALS';
    value: string;
    values: null;
  }> = [{ field: 'scheduleDate', operator: 'EQUALS', value: date, values: null }];
  if (plannerId) {
    filters.push({ field: 'counselorId', operator: 'EQUALS', value: plannerId, values: null });
  }
  const query = {
    filterGroups: [{ logic: 'AND' as const, filters }],
    sortBy: 'scheduleDate' as const,
    sortOrder: 'ASC' as const,
    pageSize: 50,
  };
  const result = await myVisitCounselApi.searchMyVisitCounsels(query);

  const sorted = [...result.items].sort((a, b) =>
    a.scheduleTime < b.scheduleTime ? -1 : a.scheduleTime > b.scheduleTime ? 1 : 0
  );

  const uniqueOrderIds = [...new Set(sorted.map((c) => c.orderId))];
  const orderMap = new Map<
    string,
    { misoOrderId: string; location: DepartureLocationInfo | null; supplyArea?: number }
  >();

  if (uniqueOrderIds.length > 0) {
    const orders = await orderApi.getOrdersByIds(uniqueOrderIds);
    for (const order of orders) {
      orderMap.set(order.id, {
        misoOrderId: order.misoOrderId,
        location: order.assignedDepartureLocation ?? null,
        supplyArea: order.moving?.departure?.supplyArea,
      });
    }
  }

  return sorted.map((c) => {
    const orderInfo = orderMap.get(c.orderId);
    const supplyArea = c.supplyArea ?? orderInfo?.supplyArea;
    return {
      visitCounselId: c.id,
      orderId: c.orderId,
      scheduleTime: c.scheduleTime,
      location: orderInfo?.location ?? null,
      misoOrderId: orderInfo?.misoOrderId,
      supplyArea: supplyArea != null ? supplyArea : undefined,
      state: c.state,
      startedAt: c.startedAt,
    };
  });
}

/**
 * PLANNER 전용: 현재 로그인한 플래너의 특정 날짜 상담 리스트
 * /api/v1/visit-counsels/search/my 사용 (PLANNER 전용)
 * @param plannerId - counselorId 필터 (서버 자동 주입이 안 될 때 FE에서 명시 전달)
 */
export function useMyPlannerDayCounsels(
  date: string,
  enabled: boolean,
  plannerId?: string
): UseMyPlannerDayCounselsResult {
  const { data, error, isLoading, mutate } = useSWR<MyPlannerDayCounsel[]>(
    enabled && date ? ['my-planner-day-counsels', date, plannerId] : null,
    () => fetchMyPlannerDayCounsels(date, plannerId),
    { revalidateOnMount: true }
  );

  return {
    counsels: data ?? [],
    isLoading,
    error: error ? (error instanceof Error ? error : new Error('상담 조회 실패')) : null,
    refetch: () => mutate(),
  };
}
